import { Component, inject, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder, FormsModule, ReactiveFormsModule,
  ValidationErrors, ValidatorFn, Validators
} from '@angular/forms';
import { EmrInputComponent } from "../../../../../shared/emr-input/emr-input.component";
import { TableModule } from 'primeng/table';
import { PatientDashboardTabComponent } from '../../../dashboard-tab/dashboard-tab.component';
import { PatientDashboardStore } from 'src/app/patients/state/dashboard-page.store';
import { Store } from '@ngrx/store';
import { DialogService } from 'primeng/dynamicdialog';
import { ProviderOrder } from 'src/app/calvin-emr-shared/models/provider-order.model';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { OrderViewDialogComponent } from '../../provider-orders-tab/order-view-dialog/order-view-dialog.component';
import { OrderSet } from 'src/app/calvin-emr-shared/models/order-set.model';
import { Note } from 'src/app/calvin-emr-shared/models/note.model';
import { ButtonDirective } from 'primeng/button';
import { HtmlDialogComponent } from 'src/app/shared/html-dialog/html-dialog.component';
import { NURSING_ORDERS_FIELD, PROVIDER_ORDERS_FIELD } from 'src/app/global';
import { OrderEditDialogComponent } from '../../provider-orders-tab/order-edit-dialog/order-edit-dialog.component';
import { TooltipModule } from 'primeng/tooltip';
import { NoteDialogComponent } from '../../note-dialog/note-dialog.component';

@Component({
  selector: 'app-order-set-form',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    EmrInputComponent,
    ButtonDirective,
    TooltipModule,
  ],
  standalone: true,
  templateUrl: './order-set-form.component.html',
  styleUrl: './order-set-form.component.scss'
})
export class OrderSetFormComponent extends PatientDashboardTabComponent implements OnInit {

  @Input() orderSet?: OrderSet;     // provided if we are editing; undefined, if we are creating.

  fb = inject(FormBuilder);
  dialogService = inject(DialogService);

  providerOrders: ProviderOrder[] = [];
  providerOrdersForThisOrderSet: ProviderOrder[] = [];
  nursingOrders: Note[] = [];
  nursingOrdersForThisOrderSet: Note[] = [];

  /**
   * Form used to hold information about an order set.
   */
  form = this.fb.group({
    name: ['', Validators.required],
    providerOrders: this.fb.array([]),
    nursingOrders: this.fb.array([]),
  }, { validators: POorNOChosenValidator });

  constructor(
    patientStore: PatientDashboardStore,
    store: Store,
  ) {
    super(patientStore, store);
    this.patient$.subscribe((patient: Patient) => {
      this.nursingOrders = patient.nursingOrders;
      this.providerOrders = patient.providerOrders;
    });
  }


  ngOnInit(): void {
    if (this.orderSet) {
      this.providerOrdersForThisOrderSet = this.providerOrders.filter((po: ProviderOrder) =>
        this.orderSet!.providerOrderIds.includes(po.id));
      this.nursingOrdersForThisOrderSet = this.nursingOrders.filter((no: Note) =>
        this.orderSet!.nursingOrderIds.includes(no.id));
      this.prefillForm();
    }
  }

  /**
   * If the form is specified to be prefilled (for example, when an order set is being edited),
   * set all values in the form to match those of the provided Order Set. List all
   * possible provider orders and nursing orders.
   */
  prefillForm() {
    // Set the name
    this.form.patchValue({
      name: this.orderSet!.name,
    });

    // Clear and populate provider orders FormArray
    this.providerOrdersFormArray.clear();
    this.providerOrdersForThisOrderSet.forEach(order => {
      this.providerOrdersFormArray.push(this.fb.control(order));
    });

    // Clear and populate nursing orders FormArray if needed
    this.nursingOrdersFormArray.clear();
    this.nursingOrdersForThisOrderSet.forEach(order => {
      this.nursingOrdersFormArray.push(this.fb.control(order));
    });
  }

  viewProviderOrder(order: ProviderOrder) {
    this.dialogService.open(OrderViewDialogComponent, {
      header: 'View Order',
      data: order,
      width: '50%',
    });
  }

  viewNursingOrder(order: Note) {
    const dialogRef = this.dialogService.open(HtmlDialogComponent, {
      header: order.title,
      data: order,
      width: '50%',
    });
  }

  deleteProviderOrder(order: ProviderOrder) {
    this.providerOrdersForThisOrderSet = this.providerOrdersForThisOrderSet.filter((o) => {
      return o.id !== order.id;
    });
    this.providerOrders = this.providerOrders.filter((o) => {
      return o.id !== order.id;
    });

    // Update the FormArray
    const index = this.providerOrdersFormArray.controls.findIndex(control =>
      control.value.id === order.id);
    if (index > -1) {
      this.providerOrdersFormArray.removeAt(index);
    }

    this.updatePatientField(PROVIDER_ORDERS_FIELD, this.providerOrders);
  }

  deleteNursingOrder(order: Note) {
    this.nursingOrdersForThisOrderSet = this.nursingOrdersForThisOrderSet.filter((o) => {
      return o.id !== order.id;
    });
    this.nursingOrders = this.nursingOrders.filter((o) => {
      return o.id !== order.id;
    });

    // Update the FormArray
    const index = this.nursingOrdersFormArray.controls.findIndex(control =>
      control.value.id === order.id);
    if (index > -1) {
      this.nursingOrdersFormArray.removeAt(index);
    }

    this.updatePatientField(NURSING_ORDERS_FIELD, this.nursingOrders);
  }


  /**
   * Add a nursing order to the order set
   */
  addNursingOrder(order: Note) {
    // Check if the order is already added to avoid duplicates
    const isAlreadyAdded = this.nursingOrdersFormArray.value.some((no: Note) => no.id === order.id);
    if (!isAlreadyAdded) {
      this.nursingOrdersFormArray.push(this.fb.control(order));
    }
  }

  /**
   * Remove a nursing order from the order set
   */
  removeNursingOrder(order: Note) {
    const index = this.nursingOrdersFormArray.controls.findIndex(control =>
      control.value.id === order.id);
    if (index > -1) {
      this.nursingOrdersFormArray.removeAt(index);
    }
  }

  openProviderOrderDialog() {
    const dialogRef = this.dialogService.open(OrderEditDialogComponent, {
      header: "Create Provider Order for Order Set",
      data: {
        type: 'create',
        forOrderSet: true,
      },
      width: '90%',
    });

    // Create the Provider Order that will be part of this order set.
    dialogRef.onClose.subscribe((order: ProviderOrder) => {
      if (order) {
        this.providerOrdersForThisOrderSet.push(order);
        this.providerOrders.push(order);    // add to list of all provider orders.

        // Update the FormArray
        this.providerOrdersFormArray.push(this.fb.control(order));

        this.updatePatientField(PROVIDER_ORDERS_FIELD, this.providerOrders);
      }
    });
  }

  openNursingOrderDialog() {
    const dialogRef = this.dialogService.open(NoteDialogComponent, {
      header: "Create Nursing Order for Order Set",
      data: {
        type: 'create',
        forOrderSet: true,
      },
      width: '90%',
    });

    // Create the Nursing Order that will be part of this order set.
    dialogRef.onClose.subscribe((order: Note) => {
      if (order) {
        this.nursingOrdersForThisOrderSet.push(order);
        this.nursingOrders.push(order);    // add to list of all nursing orders.

        // Update the FormArray
        this.nursingOrdersFormArray.push(this.fb.control(order));

        this.updatePatientField(NURSING_ORDERS_FIELD, this.nursingOrders);
      }
    });
  }

  /**
   * Retrieves and formats data from the form to create or update an OrderSet object.
   */
  public createOrderSetFromForm(): OrderSet {

    // Order Set already exists: we are editing it.
    let id = undefined;
    if (this.orderSet) {
      id = this.orderSet.id;
    }

    const providerOrderIds = this.providerOrdersForThisOrderSet.map(po => po.id);
    // Get selected nursing orders from the selectedNursingOrders array
    const nursingOrderIds = this.nursingOrdersForThisOrderSet.map(no => no.id);

    return new OrderSet({
      id,
      name: this.form.controls.name.value!,
      providerOrderIds,
      nursingOrderIds,
    });
  }

  /**
 * Get the provider orders FormArray
 */
  get providerOrdersFormArray() {
    return this.form.get('providerOrders') as FormArray;
  }

  /**
   * Get the nursing orders FormArray
   */
  get nursingOrdersFormArray() {
    return this.form.get('nursingOrders') as FormArray;
  }

  /**
   * Check if the form has the "no orders selected" validation error
   */
  get hasNoOrdersError(): boolean {
    return this.form.hasError('noOrdersSelected');
  }

  /**
   * Get the validation error message for display
   */
  get validationErrorMessage(): string {
    if (this.hasNoOrdersError) {
      return 'Please select at least one Provider Order or Nursing Order.';
    }
    return '';
  }

}

/**
 * Check if the user has chosen at least one Provider Order or Nursing Order.
 * @param control
 * @returns null if the form is valid, ValidationErrors if invalid
 */
export const POorNOChosenValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {

  const providerOrdersArray = control.get('providerOrders');
  const nursingOrdersArray = control.get('nursingOrders');

  // Check if at least one of the arrays has content
  const hasProviderOrders = providerOrdersArray && providerOrdersArray.value && providerOrdersArray.value.length > 0;
  const hasNursingOrders = nursingOrdersArray && nursingOrdersArray.value && nursingOrdersArray.value.length > 0;

  // If neither has any orders, return validation error
  if (!hasProviderOrders && !hasNursingOrders) {
    return { noOrdersSelected: true };
  }

  return null;
}

